<template>
  <component
    :is="withContainer ? 'ChiftContainer' : 'div'"
    v-bind="withContainer ? { size: 'extra-small' } : {}"
  >
    <div v-if="$can('send_invoice_to_accounting') && invoice.company.chift">
      <!--
        The failure line sits outside the sent/not-sent branches below on
        purpose: chift_send_failure is only exposed while the invoice carries no
        chift_id, so nesting it under the "sent" branch would hide it for good.
      -->
      <div
        v-if="chiftSendFailure"
        class="mb-1 flex items-start gap-1.5 rounded px-1.5 text-xs"
      >
        <span class="flex-1 font-semibold text-amber-600 capitalize-first">
          {{ $t('invoicing.chift_send_failure_title') }}
        </span>
        <ChiftSendFailurePopover
          class="shrink-0"
          :failure="chiftSendFailure"
          @show-history="showHistory = true"
        />
      </div>
      <div
        v-if="invoice.chift_id"
        class="flex gap-2 rounded px-1.5 font-semibold text-emerald-500"
      >
        <span><FontAwesomeIcon :icon="['fas', 'check-circle']" /></span>
        <span>{{ $t('global.sended') }}</span>
      </div>
      <template v-else>
        <BaseButton
          v-if="chiftSendOptions.type === 'manually'"
          theme-color="bg-secondary text-contrast-secondary"
          :title="$t('button.send_to_accounting')"
          icon="paper-plane"
          class="w-full"
          size="extra-small"
          inverse-icon-order
          :display-loader="sending"
          :disabled="sending || disabled"
          data-faro-user-action-name="invoice.send_accounting"
          @click="sendToAccounting"
        />
        <div v-else class="flex flex-col gap-2">
          <div
            class="flex gap-2 rounded border border-dashed border-emerald-500 bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-500"
          >
            <span><FontAwesomeIcon :icon="['fas', 'alarm-clock']" /></span>
            <span>
              {{
                sending_options.find(
                  (sending_option) =>
                    sending_option.id === chiftSendOptions.type
                )?.name ?? chiftSendOptions.type
              }}
            </span>
          </div>
          <BaseButton
            theme-color="bg-secondary text-contrast-secondary"
            :title="$t('button.send_to_accounting')"
            icon="paper-plane"
            class="w-full"
            size="extra-small"
            inverse-icon-order
            :display-loader="sending"
            :disabled="sending || disabled"
            data-faro-user-action-name="invoice.send_accounting"
            @click="sendToAccounting"
          />
        </div>
        <div v-if="invoice.chift_id">
          {{ $t('global.pending_accounting') }}
        </div>
      </template>

      <ChiftSendAttemptsModal
        v-if="showHistory"
        :invoice-id="invoice.id"
        @close="showHistory = false"
      />
    </div>
  </component>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import ChiftContainer from '@c/Partner/Chift/ChiftContainer.vue'
import ChiftSendAttemptsModal from '@c/Partner/Chift/ChiftSendAttemptsModal.vue'
import ChiftSendFailurePopover from '@c/Partner/Chift/ChiftSendFailurePopover.vue'

export default {
  name: 'ChiftAccountingInvoiceComponent',
  components: {
    ChiftContainer,
    ChiftSendAttemptsModal,
    ChiftSendFailurePopover,
    FontAwesomeIcon,
    BaseButton
  },
  props: {
    invoice: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    withContainer: {
      type: Boolean,
      default: true
    }
  },
  emits: ['success'],
  data() {
    return {
      sending: false,
      showHistory: false
    }
  },
  computed: {
    ...mapGetters({
      sending_options: 'metadata/getChiftSendingOptions'
    }),
    chiftSendOptions() {
      return this.invoice.account_diary?.settings.chift.send_options
    },
    /**
     * Only exposed by InvoiceResource while an unresolved failure exists, so
     * its mere presence is the condition to surface it.
     */
    chiftSendFailure() {
      return this.invoice.chift_send_failure
    }
  },
  methods: {
    ...mapActions({
      processSendToAccountingAction: 'invoicing/sendToAccounting'
    }),
    sendToAccounting() {
      this.sending = true
      this.processSendToAccountingAction({
        invoice_id: this.invoice.id
      })
        .then((response) => {
          if (response.success) {
            this.$h.toastSuccess(this.$t('global.sent_to_accounting'))
          } else {
            this.$h.toastDanger(response.message)
          }

          // Refresh on both branches. The backend records the failed attempt
          // before answering, so a failure is exactly when the invoice needs to
          // be refetched: the toast is transient, the diagnostic is not.
          this.$emit('success', true)
        })
        .finally(() => {
          this.sending = false
        })
    }
  }
}
</script>
